import { useNavigate } from '@tanstack/react-router';
import { ArrowDownToLine, ClipboardList, Hourglass, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpsLookupTransactionRow, ReviewQueueRow, WithdrawalQueueRow } from '@pf/contracts';
import { useOpsLookup } from '@/lib/api/ops-queries';
import { formatDateTime } from '@/lib/datetime';
import { useDebouncedParam, useListState } from '@/lib/nav/list-state';
import { LOOKUP_LIST, withdrawalQueueHref } from '@/lib/nav/ops-lists';
import { REVIEW_REASON_META } from '@/lib/states';
import { shortId } from '@/lib/utils';
import { MoneyDisplay } from '@/components/domain/money-display';
import { ParticipantChip } from '@/components/domain/participant-chip';
import { StateBadge } from '@/components/domain/state-badge';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader, Section } from '@/components/layout/page';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import { Input, Label } from '@/components/ui/primitives';

/**
 * The unified lookup (`/ops/lookup`) — one box that answers "where is this customer's money right
 * now", across the three tables it can be sitting in: a deposit at any point in its lifecycle, a
 * withdrawal-queue row, or an open review case.
 *
 * The server decides what the pasted text *is* (`query_kind` — a transaction id, a withdrawal id,
 * a lock reference, an amount, or an account) and this page only echoes that decision back and
 * renders whichever of the three buckets came back non-empty. It exists because an operator who
 * is handed a customer's complaint rarely knows which of History, the Withdrawal queue or the
 * Review queue the money is in right now — today they have to guess a screen, guess a filter, and
 * try again when they guessed wrong. This is the one box that used to take three.
 *
 * Deliberately not paginated the way the three list screens it draws from are: each bucket is
 * capped server-side (`limit`, default 25) and shows "แสดง X จาก Y" when the true count is
 * larger, which is the honest signal that a query this broad belongs on its own screen with real
 * filters — History, the Withdrawal queue or the Review queue — rather than on a lookup meant for
 * one customer's row.
 */
export function LookupPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const view = useListState(LOOKUP_LIST);
  const [draft, onDraftChange] = useDebouncedParam(
    view.state.q,
    (next) => {
      view.set({ q: next });
    },
    400,
  );

  const lookup = useOpsLookup({ q: view.state.q });
  const hasQuery = view.state.q.trim().length > 0;

  const transactionColumns: Column<OpsLookupTransactionRow>[] = [
    {
      id: 'id',
      header: t('opsLookup.transactions.columns.id'),
      priority: 'primary',
      cell: (row) => <span className="font-mono text-xs font-medium">{shortId(row.id)}</span>,
    },
    {
      id: 'web',
      header: t('opsLookup.transactions.columns.web'),
      priority: 'secondary',
      cell: (row) => <ParticipantChip participant={row.participant} />,
    },
    {
      id: 'depositor',
      header: t('opsLookup.transactions.columns.depositor'),
      priority: 'secondary',
      cell: (row) => (
        <span className="truncate text-xs">
          {row.depositor_account_display ?? row.depositor_account_ref ?? '—'}
        </span>
      ),
    },
    {
      id: 'withdrawer',
      header: t('opsLookup.transactions.columns.withdrawer'),
      priority: 'secondary',
      cell: (row) => (
        <span className="truncate text-xs">
          {row.withdrawer_account_display ?? row.withdrawer_account_ref ?? '—'}
        </span>
      ),
    },
    {
      id: 'amount',
      header: t('opsLookup.transactions.columns.amount'),
      align: 'right',
      priority: 'primary',
      cell: (row) => <MoneyDisplay amount={row.amount} />,
    },
    {
      id: 'status',
      header: t('opsLookup.transactions.columns.status'),
      priority: 'secondary',
      // Full deposit lifecycle, not just the terminal three History shows — the whole point of
      // this bucket is answering "where is it right now", which is most useful while a row is
      // still RESERVED or VERIFYING.
      cell: (row) => <StateBadge machine="deposit" status={row.status} size="sm" />,
    },
    {
      id: 'updatedAt',
      header: t('opsLookup.transactions.columns.updatedAt'),
      priority: 'detail',
      cell: (row) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatDateTime(row.updated_at)}
        </span>
      ),
    },
  ];

  const withdrawalColumns: Column<WithdrawalQueueRow>[] = [
    {
      id: 'id',
      header: t('opsLookup.withdrawals.columns.id'),
      priority: 'primary',
      cell: (row) => <span className="font-mono text-xs font-medium">{shortId(row.id)}</span>,
    },
    {
      id: 'web',
      header: t('opsLookup.withdrawals.columns.web'),
      priority: 'secondary',
      cell: (row) => <ParticipantChip participant={row.participant} />,
    },
    {
      id: 'withdrawer',
      header: t('opsLookup.withdrawals.columns.withdrawer'),
      priority: 'secondary',
      cell: (row) => (
        <span className="truncate text-xs">{row.account_display ?? row.account_ref ?? '—'}</span>
      ),
    },
    {
      id: 'amount',
      header: t('opsLookup.withdrawals.columns.amount'),
      align: 'right',
      priority: 'primary',
      cell: (row) => {
        // A shrunk parent (split fill, migration 0034): `origin_amount` is what the website
        // actually locked, `amount` is what is left after slices were cut off it. Mirrors the
        // same contrast the Withdrawal queue itself renders, so a row found here reads exactly
        // as it would on that screen.
        const originAmount = row.origin_amount;
        const isShrunkParent =
          row.origin_withdrawal_id == null &&
          originAmount != null &&
          originAmount.value !== row.amount.value;
        if (!isShrunkParent) return <MoneyDisplay amount={row.amount} />;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <MoneyDisplay
              amount={originAmount}
              size="sm"
              className="text-muted-foreground line-through"
            />
            <span aria-hidden="true" className="text-2xs text-muted-foreground">
              →
            </span>
            <MoneyDisplay amount={row.amount} />
          </div>
        );
      },
    },
    {
      id: 'status',
      header: t('opsLookup.withdrawals.columns.status'),
      priority: 'secondary',
      cell: (row) => <StateBadge machine="withdrawal" status={row.status} size="sm" />,
    },
    {
      id: 'matchedTransaction',
      header: t('opsLookup.withdrawals.columns.matchedTransaction'),
      priority: 'detail',
      cell: (row) =>
        row.matched_transaction_id === null ? (
          <span className="text-2xs text-muted-foreground">—</span>
        ) : (
          <span className="font-mono text-2xs">{shortId(row.matched_transaction_id)}</span>
        ),
    },
    {
      id: 'updatedAt',
      header: t('opsLookup.withdrawals.columns.updatedAt'),
      priority: 'detail',
      cell: (row) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatDateTime(row.updated_at)}
        </span>
      ),
    },
  ];

  const reviewCaseColumns: Column<ReviewQueueRow>[] = [
    {
      id: 'case',
      header: t('opsLookup.reviewCases.columns.id'),
      priority: 'primary',
      cell: (row) => <span className="font-mono text-xs font-medium">{shortId(row.case.id)}</span>,
    },
    {
      id: 'status',
      header: t('opsLookup.reviewCases.columns.status'),
      priority: 'secondary',
      cell: (row) => <StateBadge machine="review-case" status={row.case.status} size="sm" />,
    },
    {
      id: 'reason',
      header: t('opsLookup.reviewCases.columns.reason'),
      priority: 'secondary',
      cell: (row) => {
        const meta = REVIEW_REASON_META[row.case.reason];
        return (
          <span className="text-xs font-medium">
            {meta ? t(meta.labelKey) : row.case.reason}
          </span>
        );
      },
    },
    {
      id: 'transactionId',
      header: t('opsLookup.reviewCases.columns.transactionId'),
      priority: 'detail',
      cell: (row) => (
        <span className="font-mono text-2xs text-muted-foreground">
          {shortId(row.case.transaction_id)}
        </span>
      ),
    },
    {
      id: 'amount',
      header: t('opsLookup.reviewCases.columns.amount'),
      align: 'right',
      priority: 'primary',
      cell: (row) => <MoneyDisplay amount={row.context.amount} />,
    },
    {
      id: 'updatedAt',
      header: t('opsLookup.reviewCases.columns.updatedAt'),
      priority: 'detail',
      cell: (row) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatDateTime(row.case.opened_at)}
        </span>
      ),
    },
  ];

  const data = lookup.data;
  const noResultsAtAll =
    hasQuery &&
    data?.transactions.items.length === 0 &&
    data?.withdrawals.items.length === 0 &&
    data?.review_cases.items.length === 0;

  return (
    <>
      <PageHeader title={t('opsLookup.title')} description={t('opsLookup.description')} />

      <div className="max-w-2xl space-y-1.5">
        <Label htmlFor="lookup-search">{t('opsLookup.searchLabel')}</Label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="lookup-search"
            className="pl-9"
            placeholder={t('opsLookup.searchPlaceholder')}
            value={draft}
            onChange={(event) => {
              onDraftChange(event.target.value);
            }}
          />
        </div>
      </div>

      {!hasQuery ? (
        <EmptyState
          icon={<Search className="size-5" aria-hidden="true" />}
          title={t('opsLookup.empty.title')}
          description={t('opsLookup.empty.description')}
        />
      ) : lookup.isLoading ? (
        <TableSkeleton rows={5} />
      ) : lookup.isError ? (
        <ErrorState
          error={lookup.error}
          onRetry={() => {
            void lookup.refetch();
          }}
        />
      ) : data === undefined ? null : (
        <div className="space-y-8">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {t('opsLookup.queryKindLine', { label: t(`opsLookup.queryKind.${data.query_kind}`) })}
          </p>

          {noResultsAtAll ? (
            <EmptyState
              icon={<Search className="size-5" aria-hidden="true" />}
              title={t('opsLookup.noResults.title')}
              description={t('opsLookup.noResults.description')}
            />
          ) : (
            <>
              <Section
                title={t('opsLookup.transactions.title')}
                description={
                  data.transactions.total > data.transactions.items.length
                    ? t('opsLookup.shownOfTotal', {
                        shown: data.transactions.items.length,
                        total: data.transactions.total,
                      })
                    : t('opsLookup.sectionCount', { count: data.transactions.total })
                }
              >
                <DataTable
                  caption={t('opsLookup.transactions.title')}
                  columns={transactionColumns}
                  rows={data.transactions.items}
                  rowKey={(row) => row.id}
                  onRowClick={(row) => {
                    void navigate({
                      to: '/ops/history/$transactionId',
                      params: { transactionId: row.id },
                    });
                  }}
                  empty={
                    <EmptyState
                      icon={<ArrowDownToLine className="size-5" aria-hidden="true" />}
                      title={t('opsLookup.transactions.empty.title')}
                      description={t('opsLookup.transactions.empty.description')}
                    />
                  }
                />
              </Section>

              <Section
                title={t('opsLookup.withdrawals.title')}
                description={
                  data.withdrawals.total > data.withdrawals.items.length
                    ? t('opsLookup.shownOfTotal', {
                        shown: data.withdrawals.items.length,
                        total: data.withdrawals.total,
                      })
                    : t('opsLookup.sectionCount', { count: data.withdrawals.total })
                }
              >
                <DataTable
                  caption={t('opsLookup.withdrawals.title')}
                  columns={withdrawalColumns}
                  rows={data.withdrawals.items}
                  rowKey={(row) => row.id}
                  onRowClick={(row) => {
                    void navigate({ href: withdrawalQueueHref(row.id, row.status) });
                  }}
                  empty={
                    <EmptyState
                      icon={<Hourglass className="size-5" aria-hidden="true" />}
                      title={t('opsLookup.withdrawals.empty.title')}
                      description={t('opsLookup.withdrawals.empty.description')}
                    />
                  }
                />
              </Section>

              <Section
                title={t('opsLookup.reviewCases.title')}
                description={
                  data.review_cases.total > data.review_cases.items.length
                    ? t('opsLookup.shownOfTotal', {
                        shown: data.review_cases.items.length,
                        total: data.review_cases.total,
                      })
                    : t('opsLookup.sectionCount', { count: data.review_cases.total })
                }
              >
                <DataTable
                  caption={t('opsLookup.reviewCases.title')}
                  columns={reviewCaseColumns}
                  rows={data.review_cases.items}
                  rowKey={(row) => row.case.id}
                  onRowClick={(row) => {
                    void navigate({
                      to: '/ops/reviews/$caseId',
                      params: { caseId: row.case.id },
                    });
                  }}
                  empty={
                    <EmptyState
                      icon={<ClipboardList className="size-5" aria-hidden="true" />}
                      title={t('opsLookup.reviewCases.empty.title')}
                      description={t('opsLookup.reviewCases.empty.description')}
                    />
                  }
                />
              </Section>
            </>
          )}
        </div>
      )}
    </>
  );
}
